/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * HTTP interceptor that normalises every `HttpErrorResponse` into a typed
 * application error before it reaches any service or component.
 *
 * Processing pipeline:
 * 1. Passes the request through to the next handler unchanged.
 * 2. On a successful response, the observable is forwarded without modification.
 * 3. On an `HttpErrorResponse` (status === 0 or 4xx / 5xx):
 *    a. Delegates parsing to `ErrorTransformerService.transform()`.
 *    b. Wraps the result in the most specific `AppError` subclass:
 *       - `NetworkError`    — status 0 (no server reached)
 *       - `AuthError`       — status 401 or 403
 *       - `ValidationError` — status 400
 *       - `ApiError`        — all other 4xx / 5xx
 *    c. Re-throws the typed error via `throwError(() => ...)`.
 * 4. Any non-`HttpErrorResponse` error (e.g. RxJS internal, client-side JS
 *    exception) is re-thrown unchanged so it is not silently swallowed.
 *
 * Related Modules:
 * - `ErrorTransformerService`   — maps raw bodies to `NormalisedApiError`
 * - `api-error.ts`              — `AppError` class hierarchy
 * - `GlobalErrorHandlerService` — terminal consumer of the typed errors
 *
 * Registration:
 * Register via `HTTP_INTERCEPTORS` in `AppModule` providers with `multi: true`.
 *
 * @see {@link https://angular.dev/guide/http/interceptors} Angular HTTP Interceptors
 */

import {inject} from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import {catchError, throwError} from 'rxjs';
import {ErrorTransformerService} from './error-transformer.service';
import {
  ApiError,
  AppError,
  AuthError,
  NetworkError,
  ValidationError,
} from '../models/errors/api-error';

/**
 * Builds the most specific `AppError` subclass for a given `HttpErrorResponse`.
 *
 * Decision tree (evaluated in order):
 * | Condition                      | Error class       |
 * |--------------------------------|-------------------|
 * | `status === 0`                 | `NetworkError`    |
 * | `status === 401 \|\| 403`      | `AuthError`       |
 * | `status === 400`               | `ValidationError` |
 * | any other HTTP error           | `ApiError`        |
 *
 * @param httpError    - The raw Angular `HttpErrorResponse`.
 * @param transformer  - Service that maps the raw body to `NormalisedApiError`.
 * @returns A typed `AppError` subclass instance.
 */
function buildTypedError(
  httpError: HttpErrorResponse,
  transformer: ErrorTransformerService,
): AppError {
  // Network error — request never reached the server (e.g. offline, CORS)
  if (httpError.status === 0) {
    return new NetworkError(httpError.error);
  }

  const normalisedError = transformer.transform(
    httpError.status,
    httpError.error,
  );

  if (
    httpError.status === HttpStatusCode.Unauthorized ||
    httpError.status === HttpStatusCode.Forbidden
  ) {
    const authStatus = httpError.status;
    return new AuthError(
      authStatus,
      normalisedError.detail,
      httpError.error,
      normalisedError,
    );
  }

  if (httpError.status === HttpStatusCode.BadRequest) {
    return new ValidationError(normalisedError, httpError.error);
  }

  return new ApiError(
    httpError.status,
    normalisedError.detail,
    httpError.error,
    normalisedError,
  );
}

/**
 * Functional HTTP interceptor that converts every `HttpErrorResponse` into a
 * typed `AppError` subclass before the error reaches any downstream consumer.
 *
 * Successful HTTP responses pass through without modification.
 * Non-`HttpErrorResponse` errors are re-thrown as-is.
 *
 * Usage (register in `AppModule`):
 * ```TypeScript
 * {
 *   provide: HTTP_INTERCEPTORS,
 *   useValue: errorResponseInterceptor,
 *   multi: true,
 * }
 * ```
 */
export const errorResponseInterceptor: HttpInterceptorFn = (req, next) => {
  const transformer = inject(ErrorTransformerService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        return throwError(() => buildTypedError(error, transformer));
      }

      // Non-HTTP errors (e.g. client-side JS exceptions) are passed through
      // unchanged so they are handled by GlobalErrorHandlerService.
      return throwError(() => error);
    }),
  );
};
